import { PublicLayout } from "../../components/public-layout.js";

export function PrivacyPage() {
  return (
    <PublicLayout hideFooter={false}>
      <div className="legal-page">
        <h1>Privacy Policy</h1>

        <p>
          <strong>Last updated:</strong> March 2025
        </p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            AccessiMonitor ("we," "us," "our," or "Company") is committed to
            protecting your privacy. This Privacy Policy explains how we
            collect, use, disclose, and safeguard your information when you
            visit our website and use our services.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>

          <h3>A. Information You Provide</h3>
          <ul>
            <li>Account information (name, email, password)</li>
            <li>Website URLs you add for scanning</li>
            <li>
              Billing and payment information (processed securely via Stripe)
            </li>
            <li>Support and communication messages</li>
          </ul>

          <h3>B. Information We Collect Automatically</h3>
          <ul>
            <li>Log data (IP address, browser type, pages visited)</li>
            <li>Device information (device type, operating system)</li>
            <li>Scan data (pages crawled, findings detected, scan timing)</li>
            <li>Cookies and tracking technologies</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use collected information to:</p>
          <ul>
            <li>Provide and improve our scanning and reporting services</li>
            <li>Process transactions and send related information</li>
            <li>Send technical notices and support messages</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Monitor and analyze usage and performance</li>
            <li>Detect and prevent fraudulent activity</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to
            protect your information against unauthorized access, alteration,
            disclosure, or destruction. Your data is encrypted in transit
            (TLS/SSL) and at rest. Payment information is processed securely by
            Stripe and is never stored on our servers.
          </p>
        </section>

        <section>
          <h2>5. Data Retention</h2>
          <p>
            We retain your information for as long as your account is active or
            as needed to provide services. You may request deletion of your
            account and associated data by contacting us. Some information may
            be retained for legal or compliance purposes.
          </p>
        </section>

        <section>
          <h2>6. Your Rights and Choices</h2>
          <ul>
            <li>Access, correct, or delete your personal information</li>
            <li>Opt out of marketing communications</li>
            <li>Control cookie preferences (subject to essential cookies)</li>
            <li>Request data export or portability</li>
            <li>Exercise rights under GDPR, CCPA, and similar laws</li>
          </ul>
          <p>
            Contact us at privacy@accessimonitor.app to exercise these rights.
          </p>
        </section>

        <section>
          <h2>7. Third-Party Services</h2>
          <p>
            We use third-party services (Stripe for payments, Cloudflare for
            CDN, analytics providers) who have their own privacy policies. We
            are not responsible for their practices.
          </p>
        </section>

        <section>
          <h2>8. Cookies</h2>
          <p>
            We use cookies to maintain your session, remember preferences, and
            analyze usage. You can control cookies through your browser
            settings, but some functionality may be limited.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Our services are not directed to children under 13. We do not
            knowingly collect information from children. If we become aware of
            such collection, we will delete the information promptly.
          </p>
        </section>

        <section>
          <h2>10. International Transfers</h2>
          <p>
            Your information may be transferred to and stored in countries other
            than your country of residence. By using our services, you consent
            to such transfers.
          </p>
        </section>

        <section>
          <h2>11. Updates to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically. We'll notify you of
            material changes via email or prominent notice on our website.
          </p>
        </section>

        <section>
          <h2>12. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy, contact us at:
            <br />
            Email: privacy@accessimonitor.app
          </p>
        </section>

        <section>
          <h2>13. Data Controller &amp; GDPR Information (Article 13)</h2>

          <h3>Data Controller</h3>
          <p>The data controller responsible for your personal data is:</p>
          <ul>
            <li>
              <strong>Company:</strong> Irish Media Agency Ltd
            </li>
            <li>
              <strong>Address:</strong> Ireland
            </li>
            <li>
              <strong>Data Protection Officer:</strong> dpo@accessimonitor.app
            </li>
          </ul>

          <h3>Lawful Basis for Processing</h3>
          <p>
            We process your personal data under the following legal bases as
            defined in Article 6 of the General Data Protection Regulation
            (GDPR):
          </p>
          <ul>
            <li>
              <strong>Contract (Art. 6(1)(b)):</strong> Processing necessary to
              perform our contract with you, including account management,
              billing, and accessibility scanning services.
            </li>
            <li>
              <strong>Legitimate Interest (Art. 6(1)(f)):</strong> Processing
              necessary for our legitimate interests, including analytics,
              service improvement, and security monitoring.
            </li>
            <li>
              <strong>Consent (Art. 6(1)(a)):</strong> Processing based on your
              explicit consent, including marketing communications and
              non-essential cookies.
            </li>
            <li>
              <strong>Legal Obligation (Art. 6(1)(c)):</strong> Processing
              required to comply with legal obligations, including tax records
              and regulatory requirements.
            </li>
          </ul>

          <h3>Data Retention Periods</h3>
          <ul>
            <li>
              <strong>Account data:</strong> Retained while your account is
              active, plus 30 days after account deletion to allow recovery.
            </li>
            <li>
              <strong>Scan data:</strong> Retained according to your plan level
              &mdash; Free plans: 1 month; paid plans: up to 12 months.
            </li>
            <li>
              <strong>Billing records:</strong> Retained for 7 years to comply
              with tax and accounting obligations.
            </li>
            <li>
              <strong>Server logs:</strong> Retained for 90 days for security
              and troubleshooting purposes.
            </li>
          </ul>

          <h3>Supervisory Authority</h3>
          <p>
            You have the right to lodge a complaint with a supervisory
            authority. Our lead supervisory authority is the Irish Data
            Protection Commission (DPC):
          </p>
          <ul>
            <li>
              <strong>Website:</strong>{" "}
              <a
                href="https://www.dataprotection.ie"
                target="_blank"
                rel="noopener noreferrer"
              >
                dataprotection.ie
              </a>
            </li>
            <li>
              <strong>Address:</strong> 21 Fitzwilliam Square South, Dublin 2,
              D02 RD28, Ireland
            </li>
          </ul>

          <h3>Automated Decision-Making</h3>
          <p>
            We do not use automated decision-making or profiling that produces
            legal effects or similarly significantly affects you, as defined
            under Article 22 of the GDPR.
          </p>
        </section>
      </div>
    </PublicLayout>
  );
}
